import React, { useMemo } from "react";
import { Eye } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useGetAllStudentProgressQuery } from "../../services/student-progress/studentProgressApi";
import Loader from "../common/Loader";

const formatEnrollmentDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const StudentsTable = ({ selectedPlan, selectedCourse }) => {
  const navigate = useNavigate();
  const {
    data: response,
    isFetching,
    isError,
  } = useGetAllStudentProgressQuery();

  const students = useMemo(
    () =>
      (response?.data ?? []).map((student) => ({
        id: student.studentId,
        studentId: student.studentId,
        enrollmentId: student.enrollmentId,
        name: student.studentName || "-",
        email: student.email || "-",
        course: student.courseTitle || "-",
        courseId: student.courseId,
        subscription: student.subscription || "-",
        enrollmentDate: formatEnrollmentDate(student.enrollmentDate),
        progress: Number(student.progressPercentage) || 0,
      })),
    [response?.data],
  );

  const filteredStudents = students.filter((student) => {
    const matchesPlan = selectedPlan
      ? student.subscription.toLowerCase() === selectedPlan.toLowerCase()
      : true;
    const matchesCourse = selectedCourse
      ? student.course === selectedCourse
      : true;
    return matchesPlan && matchesCourse;
  });

  return (
    <section className="mt-6  bg-white max-w-[1448px] px-6 pb-7 pt-6">
      <div className="max-h-[750px] overflow-x-auto overflow-y-auto">
        <table className=" w-full border-spacing-0 border-separate">
          <thead>
            <tr className=" bg-[#FAFAFA] text-left text-base font-medium text-[#636366]">
              <th className="border-y border-l border-[#D9D9D9] px-8 py-3.5">
                Students Name
              </th>
              <th className="border-y border-[#D9D9D9] px-8 py-3.5">Email</th>
              <th className="border-y border-[#D9D9D9] px-8 py-3.5">Course</th>
              <th className="border-y border-[#D9D9D9] px-8 py-3.5">
                Subscription
              </th>
              <th className="border-y border-[#D9D9D9] px-8 py-3.5">
                Enrollment Date
              </th>
              <th className="border-y border-[#D9D9D9] px-8 py-3.5">
                Progress
              </th>
              <th className="border-y border-r border-[#D9D9D9] px-8 py-3.5">
                Action
              </th>
            </tr>
          </thead>

          <tbody>
            <tr>
              <td colSpan={7} className="h-1.5 p-0 border-0"></td>
            </tr>
            {isFetching ? (
              <tr className="bg-white">
                <td
                  colSpan={7}
                  className="border border-[#D9D9D9] h-24 px-8 py-6 text-center text-sm text-[#6B7280]"
                >
                  <div className="flex items-center justify-center">
                    <Loader size="lg" className="text-[#923D8C]" />
                  </div>
                </td>
              </tr>
            ) : isError ? (
              <tr className="bg-white">
                <td
                  colSpan={7}
                  className="border border-[#D9D9D9] px-8 py-8 text-center text-sm text-[#DC2626]"
                >
                  Unable to load student progress. Please try again.
                </td>
              </tr>
            ) : filteredStudents.length === 0 ? (
              <tr className=" bg-white">
                <td
                  colSpan={7}
                  className=" border border-[#D9D9D9] h-24 px-8 py-8 text-center text-sm text-[#6B7280]"
                >
                  No students found for selected filters.
                </td>
              </tr>
            ) : (
              filteredStudents.map((student) => (
                <tr
                  key={student.enrollmentId || student.studentId}
                  className=" bg-[#FDFDFD]  text-sm font-medium text-black"
                >
                  <td className="border-y border-l border-[#D9D9D9] px-8 py-4 capitalize">
                    {student.name}
                  </td>
                  <td className="border-y border-[#D9D9D9] px-8 py-4">
                    {student.email}
                  </td>
                  <td className="border-y border-[#D9D9D9] px-8 py-4">
                    {student.course}
                  </td>
                  <td className="border-y border-[#D9D9D9] px-8 py-4 capitalize">
                    {student.subscription}
                  </td>
                  <td className="border-y border-[#D9D9D9] px-8 py-4">
                    {student.enrollmentDate}
                  </td>
                  <td className="border-y border-[#D9D9D9] px-8 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-2.5 w-[103px] rounded-full bg-[#D5D5D5]">
                        <div
                          className="h-2.5 rounded-full bg-[#923D8C]"
                          style={{ width: `${student.progress}%` }}
                        />
                      </div>
                      <span>{student.progress}%</span>
                    </div>
                  </td>
                  <td className="border-y border-r border-[#D9D9D9] px-8 py-4">
                    <button
                      type="button"
                      aria-label={`View ${student.name}`}
                      className="text-[#111827]"
                      onClick={() =>
                        navigate(
                          `/admin/student-progress/student-overview/${student.studentId}`,
                          {
                            state: { student },
                          },
                        )
                      }
                    >
                      <Eye size={20} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default StudentsTable;
